import { Box, Text, VStack } from '@chakra-ui/react'
import { NodeProps } from '@xyflow/react'
import React from 'react'

import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { InfoIconTooltip, NodeInput } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useWebhookNode } from './hooks/useWebhookNode'

export interface WebhookNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
}

const WebhookNodeComponent: React.FC<WebhookNodeProps> = ({
  id,
  data,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const { currentUrl, handleUrlChange, handleUrlBlur, hasData } =
    useWebhookNode(id, data)
  const t = useSwitchboardT()

  return (
    <BaseNode
      label={text(t, 'switchboard.nodeLabels.webhook', 'WEBHOOK')}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <InfoIconTooltip
          title={text(
            t,
            'switchboard.nodes.webhook.tooltipTitle',
            'What to do?',
          )}
          ariaLabel={text(
            t,
            'switchboard.nodes.webhook.tooltipAria',
            'Webhook information',
          )}
          content={
            <VStack align="stretch" gap={2}>
              <Text wordBreak="break-word" overflowWrap="break-word">
                {text(
                  t,
                  'switchboard.nodes.webhook.tooltip.intro',
                  'Use this feature to send notifications to an external website or server',
                )}
              </Text>
              <Box>
                <Text fontWeight="semibold" mb={1} wordBreak="break-word">
                  {text(
                    t,
                    'switchboard.nodes.webhook.tooltip.exampleUsesHeading',
                    'Example Uses:',
                  )}
                </Text>
                <Text as="ol" pl={4} style={{ listStyleType: 'decimal' }}>
                  <li>
                    {text(
                      t,
                      'switchboard.nodes.webhook.tooltip.exampleUse1',
                      'Screen pop notification',
                    )}
                  </li>
                  <li>
                    {text(
                      t,
                      'switchboard.nodes.webhook.tooltip.exampleUse2',
                      'Log calls',
                    )}
                  </li>
                  <li>
                    {text(
                      t,
                      'switchboard.nodes.webhook.tooltip.exampleUse3',
                      'Cause an action on another site',
                    )}
                  </li>
                  <li>
                    {text(
                      t,
                      'switchboard.nodes.webhook.tooltip.exampleUse4',
                      'Create leads',
                    )}
                  </li>
                </Text>
              </Box>
              <Box>
                <Text fontWeight="semibold" mb={1}>
                  {text(
                    t,
                    'switchboard.nodes.webhook.tooltip.exampleUrlHeading',
                    'Example url',
                  )}
                </Text>
                <Text fontSize="xs" mb={1}>
                  {text(
                    t,
                    'switchboard.nodes.webhook.tooltip.curlyBracketsNote',
                    'Curly brackets will be replaced by MangoVoice with actual data.',
                  )}
                </Text>
                <Text
                  as="code"
                  fontSize="xs"
                  bg="bg.subtle"
                  p={2}
                  display="block"
                  borderRadius="sm"
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                  overflowWrap="break-word"
                  maxW="100%"
                  style={{ wordWrap: 'break-word' }}
                >
                  {`http://www.example.com/mango_notification.php?callerid={CALLER_ID}&caller_number={CALLER_NUMBER}&number_dialed={NUMBER_DIALED}&call_uid={CALL_UID}&dialed_number_name={DIALED_NUMBER_NAME}&time_utc={TIME_UTC}`}
                </Text>
              </Box>
              <Box>
                <Text fontWeight="semibold" mb={1}>
                  {text(
                    t,
                    'switchboard.nodes.common.tooltip.variablesHeading',
                    'Variables',
                  )}
                </Text>
                <VStack align="stretch" gap={1} fontSize="xs">
                  <Text wordBreak="break-word" overflowWrap="break-word">
                    <Text as="strong">CALLER_ID</Text> -{' '}
                    {text(
                      t,
                      'switchboard.nodes.common.tooltip.callerIdDesc',
                      'The caller id name of the person calling',
                    )}
                  </Text>
                  <Text wordBreak="break-word" overflowWrap="break-word">
                    <Text as="strong">CALLER_NUMBER</Text> -{' '}
                    {text(
                      t,
                      'switchboard.nodes.common.tooltip.callerNumberDesc',
                      'The number of the person calling',
                    )}
                  </Text>
                  <Text wordBreak="break-word" overflowWrap="break-word">
                    <Text as="strong">NUMBER_DIALED</Text> -{' '}
                    {text(
                      t,
                      'switchboard.nodes.common.tooltip.numberDialedDesc',
                      'The number that was dialed',
                    )}
                  </Text>
                  <Text wordBreak="break-word" overflowWrap="break-word">
                    <Text as="strong">CALL_UID</Text> -{' '}
                    {text(
                      t,
                      'switchboard.nodes.common.tooltip.callUidDesc',
                      'The unique id of the call that can be used to lookup the call record at a later time.',
                    )}
                  </Text>
                  <Text wordBreak="break-word" overflowWrap="break-word">
                    <Text as="strong">DIALED_NUMBER_NAME</Text> -{' '}
                    {text(
                      t,
                      'switchboard.nodes.common.tooltip.dialedNumberNameDesc',
                      'The name in mango of the number being called',
                    )}
                  </Text>
                  <Text wordBreak="break-word" overflowWrap="break-word">
                    <Text as="strong">TIME_UTC</Text> -{' '}
                    {text(
                      t,
                      'switchboard.nodes.common.tooltip.timeUtcDesc',
                      'The time of the Call in UTC time zone',
                    )}
                  </Text>
                </VStack>
              </Box>
            </VStack>
          }
        />
        <Box flex={1}>
          <NodeInput
            label={text(t, 'switchboard.nodes.webhook.notifyUrl', 'Notify URL')}
            value={currentUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            onBlur={handleUrlBlur}
            placeholder={text(
              t,
              'switchboard.nodes.webhook.notifyUrlPlaceholder',
              'http://www.example.com/mango_notification.php',
            )}
          />
        </Box>
      </Box>
    </BaseNode>
  )
}

export const WebhookNode = React.memo(WebhookNodeComponent, nodePropsAreEqual)
